import classNames from 'classnames'
import { uniqueId } from 'lodash'
import * as React from 'react'
import { Option } from '../../clientModels'
import { isObjectNullOrUndefined } from '../../guards'
import { Alerts } from './alerts/index'
import { CheckBox } from './checkBox'
import './checkBox.scss'
import {
  FocusableComponent,
  OptionsFormFieldProps,
} from './formComponentsInterfaces'
import { by, hasError } from './formUtilities'

interface CheckBoxesProps extends OptionsFormFieldProps {
  skipRules?: boolean
  className?: string
}

export default class CheckBoxes extends React.Component<CheckBoxesProps>
  implements FocusableComponent {
  static defaultProps = {
    showAlerts: true,
  }

  static valueTypeError = 'CheckBoxes expects an object value.'

  name: string
  inputId?: string
  labelId: string
  hintId: string
  inputs: HTMLInputElement[] = []

  constructor(props: CheckBoxesProps) {
    super(props)
    const name = (this.name = uniqueId('checkbox_'))
    this.labelId = `${name}_label`
    this.hintId = `${name}_hint`
  }

  componentDidMount() {
    const { selectedPath, path } = this.props

    if (selectedPath && path && path === selectedPath) {
      this.focus()
    }
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps: CheckBoxesProps) {
    if (
      nextProps.selectedQuestion &&
      nextProps.selectedPath &&
      nextProps.path === nextProps.selectedPath
    ) {
      this.focus()
    }
  }

  focus() {
    const inputs = this.inputs
    const first = inputs[0]
    const checked = inputs.find(i => i.checked)
    if (checked) {
      checked.focus()
    } else if (first) {
      first.focus()
    }
  }

  handleChange = (option: Option, checked: boolean) => {
    const { onChange, value, path, skipRules } = this.props

    if (!isObjectNullOrUndefined(value)) {
      throw new Error(CheckBoxes.valueTypeError)
    }

    if (onChange) {
      const nextValue: any = value ? { ...value } : {}
      if (checked) {
        nextValue[option.value] = option.label
      } else {
        delete nextValue[option.value]
      }

      onChange(nextValue, path, skipRules)
    }
  }

  handleBlur = (checkpath: string) => {
    const { path, onBlur } = this.props
    const fullpath = path ? path + '.' + checkpath : checkpath
    if (onBlur) {
      onBlur(fullpath)
    }
  }

  handleFocus = (checkpath: string) => {
    const { path, onFocus } = this.props
    const fullpath = path ? path + '.' + checkpath : checkpath
    if (onFocus) {
      onFocus(fullpath)
    }
  }

  setInput = (ref: HTMLInputElement) => this.inputs.push(ref)

  render() {
    const {
      className,
      describedBy,
      disabled,
      hint,
      label,
      labelledBy,
      messages,
      options,
      path,
      showAlerts,
      value,
    } = this.props

    const schemaOptions = options

    const ariaDescribedBy = by(describedBy, hint && this.hintId)
    const isInError = hasError(messages, path)

    if (!isObjectNullOrUndefined(value)) {
      throw new Error(CheckBoxes.valueTypeError)
    }

    return (
      <div
        className={classNames('check-boxes form-group', className, {
          disabled,
          'is-valid': !isInError,
          'is-invalid': isInError,
        })}
      >
        {label && <label id={this.labelId}>{label}</label>}
        {hint && (
          <div id={this.hintId} className='hint-text'>
            {hint}
          </div>
        )}
        {schemaOptions &&
          schemaOptions.map(option => {
            const id = `${this.name}_input_${option.value}`
            const labelId = `${this.name}_label_${option.value}`
            const ariaLabelledBy = by(
              labelledBy,
              label && this.labelId,
              labelId
            )
            // const checkpath = path ? path + '.' + option.value : option.value
            return (
              <CheckBox
                key={option.value}
                ariaDescribedBy={ariaDescribedBy}
                ariaLabelledBy={ariaLabelledBy}
                disabled={disabled}
                id={id}
                labelId={labelId}
                messages={messages}
                name={this.name}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                option={option}
                path={option.value}
                value={value}
              />
            )
          })}
        {showAlerts && <Alerts messages={messages} />}
      </div>
    )
  }
}
